import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth, api } from "@/lib/auth";
import { userAvatar } from "@/lib/avatar";
import { NAV_GROUPS } from "@/data/demo";
import * as Icons from "lucide-react";
import CommandBar from "@/components/CommandBar";

const MOBILE = [
  { label: "Home", icon: "Home", path: "/app" },
  { label: "Community", icon: "MessageSquare", path: "/app/community" },
  { label: "Copilot", icon: "Sparkles", path: "/app/ai", raised: true },
  { label: "Business", icon: "Briefcase", path: "/app/dashboard" },
  { label: "More", icon: "Menu", path: "" },
];

function Icon({ name, ...p }) { const C = Icons[name] || Icons.Circle; return <C {...p} />; }

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [cmdOpen, setCmdOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [unread, setUnread] = useState(0);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const h = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setCmdOpen(true); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  useEffect(() => {
    api.get("/notifications").then((r) => setUnread(r.data.filter((n) => !n.read).length)).catch(() => {});
  }, [loc.pathname]);

  const groups = [...NAV_GROUPS];
  if (user?.role === "admin") {
    groups.push({ section: "ADMIN", items: [{ key: "admin", label: "Admin Console", icon: "Shield", path: "/app/admin" }] });
  }

  const COMMUNITY_PATHS = ["/app/community", "/app/messages", "/app/amcs", "/app/consultants", "/app/studios"];
  const LEARN_PATHS = ["/app/learn", "/app/notebook", "/app/market", "/app/funds", "/app/regulatory"];
  const copilotCtx = () => {
    if (COMMUNITY_PATHS.includes(loc.pathname)) return "community";
    if (LEARN_PATHS.includes(loc.pathname)) return "learn";
    return "business";
  };
  const openCopilot = () => nav("/app/ai", { state: { context: copilotCtx() } });

  return (
    <div className="mfx-app min-h-screen bg-[#090A0D] flex">
      <CommandBar open={cmdOpen} setOpen={setCmdOpen} />

      {/* Sidebar */}
      <aside className={`hidden lg:flex flex-col ${collapsed ? "w-[76px]" : "w-64"} shrink-0 bg-[#0E1117] border-r border-[#222938] sticky top-0 h-screen transition-all`}>
        <div className="flex items-center gap-2 px-5 h-16 border-b border-[#222938]">
          <div className="w-9 h-9 rounded-xl grad-amber grid place-items-center text-black font-head font-bold amber-glow">M</div>
          {!collapsed && <span className="font-head font-bold text-lg tracking-tight text-slate-100">MFDX</span>}
          <button onClick={() => setCollapsed(!collapsed)} className="ml-auto text-slate-500 hover:text-slate-200" data-testid="sidebar-collapse-btn">
            <Icon name={collapsed ? "ChevronRight" : "ChevronLeft"} size={18} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto no-scrollbar py-3 px-3 space-y-4">
          <Link to="/app" data-testid="nav-home" title="Home"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${loc.pathname === "/app" ? "bg-amber-500/12 text-amber-400 font-semibold" : "text-slate-400 hover:bg-[#1A1F2B] hover:text-slate-200"}`}>
            <Icon name="Home" size={18} className={loc.pathname === "/app" ? "text-amber-400" : "text-slate-500"} />
            {!collapsed && <span>Home</span>}
          </Link>
          {groups.map((g) => {
            const expandable = !collapsed && g.items.length > 6;
            const isOpen = expanded[g.section];
            const visible = expandable && !isOpen ? g.items.slice(0, 5) : g.items;
            return (
            <div key={g.section}>
              {!collapsed && <div className="px-3 mb-2 text-base font-head font-bold uppercase tracking-wide text-amber-400" data-testid={`nav-section-${g.section.toLowerCase().replace(/\s+/g, "-")}`}>{g.section}</div>}
              <div className="space-y-1">
                {visible.map((n) => {
                  const active = loc.pathname === n.path;
                  return (
                    <Link key={n.key} to={n.path} data-testid={`nav-${n.key}`} title={n.label}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${active ? "bg-amber-500/12 text-amber-400 font-semibold" : "text-slate-400 hover:bg-[#1A1F2B] hover:text-slate-200"}`}>
                      <Icon name={n.icon} size={18} className={active ? "text-amber-400" : "text-slate-500"} />
                      {!collapsed && <span>{n.label}</span>}
                    </Link>
                  );
                })}
                {expandable && (
                  <button onClick={() => setExpanded((e) => ({ ...e, [g.section]: !e[g.section] }))} data-testid={`nav-others-${g.section.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:bg-[#1A1F2B] hover:text-slate-200 w-full transition-colors">
                    <Icon name={isOpen ? "ChevronUp" : "MoreHorizontal"} size={18} className="text-slate-500" />
                    <span>{isOpen ? "Show less" : "Others"}</span>
                  </button>
                )}
              </div>
            </div>
            );
          })}
        </nav>
        <div className="p-3 border-t border-[#222938]">
          <Link to="/app/my-mfdx" data-testid="nav-my-mfdx" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-amber-400 hover:bg-[#1A1F2B]">
            <Icon name="Compass" size={18} className="text-amber-400" />{!collapsed && "My MFDx"}
          </Link>
          <Link to="/app/profile" data-testid="nav-profile" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:bg-[#1A1F2B]">
            <Icon name="User" size={18} className="text-slate-500" />{!collapsed && "My Profile"}
          </Link>
          <button onClick={() => { logout(); nav("/"); }} data-testid="logout-btn" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:bg-[#1A1F2B] w-full">
            <Icon name="LogOut" size={18} className="text-slate-500" />{!collapsed && "Logout"}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="glass sticky top-0 z-30 h-16 flex items-center gap-3 px-4 lg:px-8">
          <div className="lg:hidden w-8 h-8 rounded-lg grad-amber grid place-items-center text-black font-head font-bold text-sm">M</div>
          <button onClick={() => setCmdOpen(true)} data-testid="command-bar-trigger"
            className="flex items-center gap-2 flex-1 max-w-md bg-[#141822] hover:bg-[#1A1F2B] border border-[#2E384D] rounded-xl px-3 py-2 text-sm text-slate-500 transition-colors">
            <Icon name="Search" size={16} />
            <span className="truncate">What do you want to get done?</span>
            <kbd className="ml-auto hidden sm:inline text-[11px] bg-[#0E1117] border border-[#2E384D] rounded px-1.5 py-0.5 text-slate-400">⌘K</kbd>
          </button>
          <div className="ml-auto flex items-center gap-3">
            <button onClick={openCopilot} data-testid="topbar-copilot" className="hidden sm:flex items-center gap-1.5 grad-amber text-black rounded-full px-3.5 py-1.5 text-sm font-semibold amber-glow hover:brightness-105 transition">
              <Icon name="Sparkles" size={15} /> Ask Copilot
            </button>
            <Link to="/app/wallet" data-testid="topbar-wallet" className="hidden sm:flex items-center gap-1.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full px-3 py-1.5 text-sm font-semibold hover:bg-emerald-500/20 transition">
              <Icon name="Wallet" size={15} /> ₹{(user?.wallet ?? 0).toLocaleString("en-IN")}
            </Link>
            <button onClick={() => nav("/app/notifications")} className="relative text-slate-400 hover:text-slate-100" data-testid="notif-btn">
              <Icon name="Bell" size={20} />
              {unread > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-black text-[10px] font-bold grid place-items-center" data-testid="notif-count">{unread}</span>}
            </button>
            <Link to="/app/profile" className="w-9 h-9 rounded-full overflow-hidden grad-amber grid place-items-center text-black font-head font-semibold text-sm" data-testid="topbar-avatar">
              {user?.avatar_path ? <img src={userAvatar(user)} alt={user?.name} className="w-full h-full object-cover" /> : (user?.name || "M")[0]}
            </Link>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 pb-28 lg:pb-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom dock */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass border-t border-[#222938] flex items-end justify-around h-[68px] px-2">
        {MOBILE.map((m) => {
          const active = loc.pathname === m.path;
          if (m.raised) {
            return (
              <button key={m.label} onClick={openCopilot} data-testid="mobile-nav-copilot"
                className="flex flex-col items-center -mt-6">
                <span className="w-14 h-14 rounded-2xl grad-amber grid place-items-center text-black amber-glow"><Icon name={m.icon} size={24} /></span>
                <span className="text-[10px] font-semibold text-amber-400 mt-1">{m.label}</span>
              </button>
            );
          }
          return (
            <button key={m.label} onClick={() => m.label === "More" ? setCmdOpen(true) : nav(m.path)} data-testid={`mobile-nav-${m.label.toLowerCase()}`}
              className={`flex flex-col items-center gap-0.5 text-[11px] font-medium pb-2 ${active ? "text-amber-400" : "text-slate-500"}`}>
              <Icon name={m.icon} size={20} /> {m.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
